/** @jsx jsx */

import { Fragment } from "react"
import PropTypes from "prop-types"
import { jsx, Heading, Flex } from "theme-ui"

import {
  PrismicRichTextFieldPropType,
  GatsbyImagePropType,
} from "../../utils/propTypes"
import { RichText } from "../RichText"
import { Divider } from "../Divider"
import { DividerType } from "../../hooks/divider"
import { ResponsiveImage } from "../ResponsiveImage"
import { Crystallize } from "../../enums/Crystallize"
import { orderInstructions } from "../../hooks/orderInstructions"
import { Paragraph } from "theme-ui"
import { formatPrice } from "../../utils/displayUtils"

const PayNowInstructions = ({ instructions, paynow_details, paynow_qr }) => (
  <Flex sx={{ flexDirection: "column", alignItems: "center" }}>
    <RichText text={instructions} />
    <Divider type={DividerType.DEFAULT_OLIVE} />
    <RichText
      sxOverrides={{
        variant: "text.headingOlive",
        textAlign: "center",
        fontSize: [3, 3, 4],
        fontWeight: "body",
      }}
      text={paynow_details}
    />
    <ResponsiveImage image={paynow_qr} sx={{ maxWidth: "100%" }} />
  </Flex>
)
PayNowInstructions.propTypes = {
  instructions: PrismicRichTextFieldPropType,
  paynow_details: PrismicRichTextFieldPropType,
  paynow_qr: GatsbyImagePropType,
}

const StripeInstructions = ({ orderStageUpdateFailed, instructions }) => (
  <Fragment>
    {orderStageUpdateFailed ? (
      <Fragment>
        <Paragraph variant="error">
          Your order was placed and your payment was successfully processed.
          However, something went wrong updating the payment status of your
          order.
        </Paragraph>
        <Paragraph variant="error">
          Please email us at{" "}
          <a href="mailto:foodscapepages@gmail.com">foodscapepages@gmail.com</a>{" "}
          with the same email you submitted in the order form, along with your
          order number, and copy and paste the error description in the previous
          paragraph so that we can rectify the issue.
        </Paragraph>
      </Fragment>
    ) : null}
    <RichText text={instructions} />
  </Fragment>
)
StripeInstructions.propTypes = {
  orderStageUpdateFailed: PropTypes.bool,
  instructions: PrismicRichTextFieldPropType,
}

export const OrderConfirmationSection = ({
  orderNumber,
  orderTotal,
  paymentMode,
  orderStageUpdateFailed,
  containerStyles,
  containerRef,
}) => {
  const {
    title,
    paynow_instructions,
    stripe_instructions,
    paynow_details,
    paynow_qr,
  } = orderInstructions()

  return (
    <div
      sx={{
        textAlign: ["left", "left", "left", "center"],
        ...containerStyles,
      }}
      ref={containerRef}
      // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
      tabIndex="0"
    >
      <Heading variant="headingOlive" sx={{ my: 5 }}>
        {title.text}
      </Heading>
      <Heading
        variant="headingOlive"
        sx={{ fontSize: [3, 3, 4], fontWeight: "body", mb: 3 }}
      >
        Your order number is: <b>{orderNumber}</b>
      </Heading>
      {paymentMode === Crystallize.PaymentMode.PAYNOW_PAYLAH ? (
        <Fragment>
          <Paragraph variant="subheading">
            Order total: {formatPrice(orderTotal)}
          </Paragraph>
          <PayNowInstructions
            instructions={paynow_instructions}
            paynow_details={paynow_details}
            paynow_qr={paynow_qr}
          />
        </Fragment>
      ) : (
        <StripeInstructions
          orderStageUpdateFailed={orderStageUpdateFailed}
          instructions={stripe_instructions}
        />
      )}
    </div>
  )
}
OrderConfirmationSection.propTypes = {
  orderNumber: PropTypes.string,
  orderTotal: PropTypes.number,
  paymentMode: PropTypes.oneOf(Object.values(Crystallize.PaymentMode)),
  orderStageUpdateFailed: PropTypes.bool,
  containerStyles: PropTypes.object,
  containerRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.any }),
  ]),
}
